import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  Typography,
  Box,
  Paper
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion } from 'framer-motion';
import styles from './FeedbackConfirmation.module.css';

/**
 * A reusable confirmation component for support forms
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the confirmation message
 * @param {string} props.message - The confirmation message
 * @param {string} props.buttonText - The text for the button (default: "Voltar para o Dashboard")
 * @param {string} props.returnPath - The path to navigate to when the button is clicked (default: "/dashboard")
 * @param {string} props.icon - The icon to display (default: "check")
 */
function FeedbackConfirmation({ 
  title, 
  message, 
  buttonText = "Voltar para o Dashboard", 
  returnPath = "/dashboard",
  icon = "check"
}) {
  const navigate = useNavigate();

  const handleReturn = () => {
    navigate(returnPath);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div 
      className={styles.container}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Paper elevation={0} className={styles.paper}>
        <Box className={styles.content}>
          <motion.div variants={itemVariants}>
            {icon === "check" && (
              <CheckCircleOutlineIcon className={styles.icon} fontSize="large" />
            )}
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Typography variant="h5" className={styles.title}>
              {title}
            </Typography>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Typography className={styles.message}>
              {message}
            </Typography>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleReturn}
              className={styles.button}
            >
              {buttonText}
            </Button>
          </motion.div>
        </Box>
      </Paper>
    </motion.div>
  );
}

export default FeedbackConfirmation;